import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";

const OWNER_TOKEN_KEY = "mateo_owner_token";
const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;

export default function OwnerLogin() {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const functionUrl = `${SUPABASE_URL}/functions/v1/verify-owner-password`;

      const response = await fetch(functionUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({ password }),
      });

      const responseText = await response.text();
      let data: Record<string, unknown> | null = null;
      try {
        data = JSON.parse(responseText);
      } catch {
        // not json
      }

      if (!response.ok) {
        setError(data?.error as string || `Server error (${response.status})`);
        return;
      }

      if (data?.valid && data?.token) {
        localStorage.setItem(OWNER_TOKEN_KEY, data.token as string);
        sessionStorage.setItem("mateo_owner_pass", password);
        navigate("/owner/dashboard", { replace: true });
      } else {
        setError((data?.error as string) || "Wrong password");
      }
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-background-50 flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block cursor-pointer">
            <span className="font-heading text-2xl font-semibold text-foreground-950">
              Mateo Teneryfa
            </span>
            <span className="block text-xs text-foreground-500 mt-1 uppercase tracking-[0.2em]">
              Owner Panel
            </span>
          </Link>
        </div>

        <div className="bg-background-50 border border-background-200/70 rounded-2xl p-6 md:p-8">
          <h2 className="text-sm font-medium text-foreground-800 mb-5 text-center">
            Enter your password to access the dashboard
          </h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-foreground-700 mb-1.5">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
                className="w-full text-sm bg-background-100 border border-background-200 rounded-lg px-3.5 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary-400"
              />
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm flex items-start gap-2">
                <i className="ri-error-warning-line mt-0.5 flex-shrink-0"></i>
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-primary-500 hover:bg-primary-600 disabled:bg-primary-400 text-background-50 dark:text-foreground-950 py-3 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  Checking...
                </>
              ) : (
                <>
                  <i className="ri-lock-line"></i>
                  Unlock Dashboard
                </>
              )}
            </button>
          </form>

          <p className="text-xs text-foreground-500 text-center mt-4">
            <Link
              to="/"
              className="hover:text-foreground-700 underline underline-offset-2 cursor-pointer"
            >
              Back to website
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}